import { useEffect, useState, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Divider, Spinner, Heading, Wrap, WrapItem, Badge,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  getUsers,
  updateUser,
  removeUser,
  getInvitations,
  inviteUser,
  revokeInvitation,
  getLocations,
  type OrganizationUserDto,
  type InvitationDto,
  type LocationDto,
} from '../api/organizationApi';

const ROLES = ['admin', 'manager', 'editor', 'viewer'] as const;

export function UsersPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<OrganizationUserDto[]>([]);
  const [invitations, setInvitations] = useState<InvitationDto[]>([]);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, invitationsData, locationsData] = await Promise.all([
        getUsers(),
        getInvitations(),
        getLocations(),
      ]);
      setUsers(usersData);
      setInvitations(invitationsData);
      setLocations(locationsData);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Flex flex={1} justify="center" align="center" p={4}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box flex={1} p={4} maxW="900px">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h2" size="lg">{t('users.title')}</Heading>
        <Button colorScheme="brand" onClick={() => setShowInvite(true)}>
          {t('users.inviteUser')}
        </Button>
      </Flex>

      {error && (
        <Flex bg="red.50" p={3} borderRadius="md" mb={3}>
          <Text color="red.500">{error}</Text>
        </Flex>
      )}
      {success && (
        <Flex bg="green.50" p={3} borderRadius="md" mb={3}>
          <Text color="green.500">{success}</Text>
        </Flex>
      )}

      {showInvite && (
        <InviteForm
          locations={locations}
          onSend={async (data) => {
            try {
              await inviteUser(data);
              setShowInvite(false);
              setSuccess(t('users.invitationSent'));
              await load();
            } catch (e: unknown) {
              setError((e as Error).message);
            }
          }}
          onCancel={() => setShowInvite(false)}
        />
      )}

      {/* Members */}
      <Heading as="h3" size="md" mb={3}>{t('users.members')}</Heading>
      {users.length === 0 ? (
        <Text color="gray.500">{t('users.noMembers')}</Text>
      ) : (
        <Box>
          {users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              locations={locations}
              onRoleChange={async (role) => {
                try {
                  await updateUser(user.id, { role });
                  setSuccess(t('users.roleUpdated'));
                  await load();
                } catch (e: unknown) {
                  setError((e as Error).message);
                }
              }}
              onRemove={async () => {
                if (!confirm(t('users.confirmRemove'))) return;
                try {
                  await removeUser(user.id);
                  setSuccess(t('users.removed'));
                  await load();
                } catch (e: unknown) {
                  setError((e as Error).message);
                }
              }}
            />
          ))}
        </Box>
      )}

      <Divider my={4} />

      {/* Pending Invitations */}
      <Heading as="h3" size="md" mb={3}>{t('users.pendingInvitations')}</Heading>
      {invitations.length === 0 ? (
        <Text color="gray.500">{t('users.noInvitations')}</Text>
      ) : (
        <Box>
          {invitations.map((inv) => (
            <Flex
              key={inv.id}
              p={3}
              borderRadius="md"
              bg="gray.50"
              gap={3}
              align="center"
              mb={2}
            >
              <Box flex={1}>
                <Text fontWeight="600">{inv.email}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t(`users.roles.${inv.role}`)} • {t(`users.invitationStatus.${inv.status}`)}
                </Text>
              </Box>
              <Badge colorScheme={inv.status === 'pending' ? 'yellow' : inv.status === 'accepted' ? 'green' : 'gray'}>
                {inv.status}
              </Badge>
              {inv.status === 'pending' && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await revokeInvitation(inv.id);
                      setSuccess(t('users.invitationRevoked'));
                      await load();
                    } catch (e: unknown) {
                      setError((e as Error).message);
                    }
                  }}
                >
                  {t('users.revoke')}
                </Button>
              )}
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

function UserCard({
  user,
  onRoleChange,
  onRemove,
}: {
  user: OrganizationUserDto;
  locations: LocationDto[];
  onRoleChange: (role: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [showRoleSelect, setShowRoleSelect] = useState(false);

  return (
    <Box p={4} borderRadius="md" bg="gray.50" mb={3}>
      <Flex justify="space-between" align="center" mb={2}>
        <Box>
          <Text fontWeight="600">{user.fullName}</Text>
          <Text fontSize="sm" color="gray.500">{user.email}</Text>
        </Box>
        <Flex gap={2}>
          <Button size="sm" onClick={() => setShowRoleSelect(!showRoleSelect)}>
            {t(`users.roles.${user.role}`)}
          </Button>
          <Button size="sm" colorScheme="red" variant="outline" onClick={onRemove}>
            {t('common.remove')}
          </Button>
        </Flex>
      </Flex>

      {showRoleSelect && (
        <Wrap spacing={2} mb={2}>
          {ROLES.map((role) => (
            <WrapItem key={role}>
              <Button
                size="sm"
                colorScheme={user.role === role ? 'brand' : 'gray'}
                variant={user.role === role ? 'solid' : 'outline'}
                onClick={async () => {
                  await onRoleChange(role);
                  setShowRoleSelect(false);
                }}
              >
                {t(`users.roles.${role}`)}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {user.locationAccess.length > 0 && (
        <Text fontSize="xs" color="gray.500">
          {t('users.locationAccess')}: {user.locationAccess.map((l) => l.name).join(', ')}
        </Text>
      )}
    </Box>
  );
}

function InviteForm({
  locations,
  onSend,
  onCancel,
}: {
  locations: LocationDto[];
  onSend: (data: { email: string; role: string; locationIds?: string[] }) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('viewer');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend({
        email,
        role,
        locationIds: selectedLocationIds.length > 0 ? selectedLocationIds : undefined,
      });
    } finally {
      setSending(false);
    }
  };

  const toggleLocation = (id: string) => {
    setSelectedLocationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <Box p={4} borderRadius="md" bg="gray.50" mb={3}>
      <Heading as="h3" size="md" mb={3}>{t('users.inviteUser')}</Heading>

      <Text fontWeight="600" mb={1}>{t('users.email')}</Text>
      <Input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="user@example.com"
        type="email"
        mb={3}
      />

      <Text fontWeight="600" mb={1}>{t('users.selectRole')}</Text>
      <Wrap spacing={2} mb={3}>
        {ROLES.map((r) => (
          <WrapItem key={r}>
            <Button
              size="sm"
              colorScheme={role === r ? 'brand' : 'gray'}
              variant={role === r ? 'solid' : 'outline'}
              onClick={() => setRole(r)}
            >
              {t(`users.roles.${r}`)}
            </Button>
          </WrapItem>
        ))}
      </Wrap>

      {locations.length > 0 && (
        <>
          <Text fontWeight="600" mb={1}>{t('users.selectLocations')}</Text>
          <Wrap spacing={2} mb={3}>
            {locations.map((loc) => (
              <WrapItem key={loc.id}>
                <Button
                  size="sm"
                  colorScheme={selectedLocationIds.includes(loc.id) ? 'brand' : 'gray'}
                  variant={selectedLocationIds.includes(loc.id) ? 'solid' : 'outline'}
                  onClick={() => toggleLocation(loc.id)}
                >
                  {loc.name}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </>
      )}

      <Flex gap={3} justify="flex-end" pt={2}>
        <Button onClick={onCancel} isDisabled={sending}>
          {t('common.cancel')}
        </Button>
        <Button colorScheme="brand" onClick={handleSend} isDisabled={sending || !email.trim()}>
          {sending ? <Spinner size="sm" /> : t('users.sendInvitation')}
        </Button>
      </Flex>
    </Box>
  );
}
